import { useCallback, useState } from 'react';

import { calculateDropMarkerOffset, reorderColumns } from '../helpers';

interface DragOverParams {
  draggedColumnKey: string;
  targetCellElement: HTMLTableCellElement | undefined;
  targetColumnKey: string;
}

interface DropParams {
  draggedColumnKey: string;
  targetColumnKey: string;
}

interface UseTableDragDropProps {
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;
}

interface UseTableDragDropReturn {
  handleColumnDragEnd: () => void;
  handleColumnDraggedOver: (params: DragOverParams) => void;
  handleColumnDropped: (params: DropParams) => void;
  isMarkerVisible: boolean;
  markerLeftOffset: number;
}

export const useTableDragDrop = ({ columnOrder, setColumnOrder }: UseTableDragDropProps): UseTableDragDropReturn => {
  const [isMarkerVisible, setMarkerVisible] = useState<boolean>(false);
  const [markerLeftOffset, setMarkerLeftOffset] = useState<number>(-1);

  const handleColumnDraggedOver = useCallback(
    ({ targetColumnKey, draggedColumnKey, targetCellElement }: DragOverParams): void => {
      if (targetColumnKey === draggedColumnKey) {
        setMarkerVisible(false);

        return;
      }

      const markerOffset: number = calculateDropMarkerOffset({
        columnOrder,
        targetColumnKey,
        draggedColumnKey,
        targetCellElement
      });

      setMarkerLeftOffset(markerOffset);
      setMarkerVisible(true);
    },
    [columnOrder]
  );

  const handleColumnDragEnd = useCallback((): void => {
    setMarkerVisible(false);
  }, []);

  const handleColumnDropped = useCallback(
    ({ targetColumnKey, draggedColumnKey }: DropParams): void => {
      setMarkerVisible(false);

      if (targetColumnKey === draggedColumnKey) {
        return;
      }

      const newColumnOrder: string[] = reorderColumns({
        columnOrder,
        targetColumnKey,
        draggedColumnKey
      });

      setColumnOrder(newColumnOrder);
    },
    [columnOrder, setColumnOrder]
  );

  return {
    isMarkerVisible,
    markerLeftOffset,
    handleColumnDraggedOver,
    handleColumnDragEnd,
    handleColumnDropped
  };
};
